import React, { useMemo } from 'react';
import { Backdrop } from '@mui/material';
import Alert from '../../Alert';
import Box from '../../Box';
import Button from '../../Button';
import CustomIcon from '../../Icon';
import Divider from '../../Divider';
import Grid from '../../Grid';
import Icon from '../../Icon';
import { MenuItem, MenuList } from '../../Menu';
import TextField from '../../TextField';
import Typography from '../../Typography';
import SearchField from '../../SearchField';
import SelectWrapper, { SelectMenuItem } from '../../Select';
import { styled, useTheme } from '../../styles';
import useTokens from '../../utils/useTokens';
import { logDeprecationWarning } from '../../utils/errorHandling';
import { DeleteIllustration } from '../../Illustrations';
import { AddCircle as AddCircleIcon, CheckCircle as CheckCircleIcon, Launch as LaunchIcon, Search as SearchIcon, Error as ErrorIcon, Back as BackIcon } from '../../Icons/Core';
import { AccountSwitcherStates, useAccountSwitcherLogic } from './useAccountSwitcherLogic';
/*
 * The AccountSwitcher component is a dropdown menu that allows users to switch between different accounts.
 * For style, it uses the account switcher tokens defined in the theme.
 * For single accounts, the default state is selected
 * For all accounts, the state is default
 */
const AccountSwitcherWrapper = styled(SelectWrapper)(({ theme }) => ({
    '&.MuiInputBase-root': {
        // Once we implement the account accounts view, this will be default for all accounts and selected for single accounts
        backgroundColor: theme.palette.navigation.accountSwitcher.color.background.selected,
        '&:hover': {
            backgroundColor: theme.palette.navigation.accountSwitcher.color.background.hover
        },
        '&.Mui-focused': {
            backgroundColor: theme.palette.navigation.accountSwitcher.color.background.focus
        },
        '&.Mui-disabled': {
            backgroundColor: theme.palette.navigation.accountSwitcher.color.background.disabled
        }
    },
    '&.MuiOutlinedInput-root': {
        '& .MuiOutlinedInput-notchedOutline': {
            // Once we implement the account accounts view, this will be default for all accounts and selected for single accounts
            borderTop: '0',
            borderLeft: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.selected}`,
            borderRight: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.selected}`,
            borderBottom: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.selected}`,
            borderTopRightRadius: theme.palette.navigation.accountSwitcher.border.radius.top,
            borderTopLeftRadius: theme.palette.navigation.accountSwitcher.border.radius.top,
            borderBottomRightRadius: theme.palette.navigation.accountSwitcher.border.radius.bottom,
            borderBottomLeftRadius: theme.palette.navigation.accountSwitcher.border.radius.bottom
        },
        '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
                borderTop: '0',
                borderLeft: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.hover}`,
                borderRight: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.hover}`,
                borderBottom: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.hover}`
            }
        },
        '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
                borderTop: '0',
                borderLeft: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.focus}`,
                borderRight: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.focus}`,
                borderBottom: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.focus}`
            }
        },
        '&.Mui-disabled': {
            '& .MuiOutlinedInput-notchedOutline': {
                borderTop: '0',
                borderLeft: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.disabled}`,
                borderRight: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.disabled}`,
                borderBottom: `${theme.palette.navigation.accountSwitcher.border.width} solid ${theme.palette.navigation.accountSwitcher.color.border.disabled}`
            }
        }
    },
    '& .MuiInputBase-input': {
        // Once we implement the account accounts view, this will be default for all accounts and selected for single accounts
        color: theme.palette.navigation.accountSwitcher.color.text.selected,
        fontFamily: theme.palette.navigation.accountSwitcher.font.family,
        fontSize: theme.palette.navigation.accountSwitcher.font.size,
        fontWeight: theme.palette.navigation.accountSwitcher.font.weight,
        lineHeight: theme.palette.navigation.accountSwitcher.font.lineHeight,
        letterSpacing: theme.palette.navigation.accountSwitcher.font.letterSpacing,
        '&:hover': {
            color: theme.palette.navigation.accountSwitcher.color.text.selected
        },
        '&.Mui-disabled': {
            color: theme.palette.navigation.accountSwitcher.color.text.disabled
        }
    }
}));
const Container = styled(Grid, {
    shouldForwardProp: prop => prop !== 'accountSwitcherState'
})(({ accountSwitcherState }) => ({
    position: 'absolute',
    /**
     * Set zIndex lower than the Dialog default zIndex (1300) so that when the Dialog pops up,
     * the AccountSwitcher will not be interactive.
     */
    zIndex: accountSwitcherState === AccountSwitcherStates.opened ? 1298 : 'auto' // Only set zIndex when opened
}));
const OpenContainer = styled(Grid, {
    shouldForwardProp: prop => prop !== 'isActionCheck'
})(({ theme, isActionCheck }) => ({
    padding: theme.palette.space.container.spacious,
    backgroundColor: useTokens().aliasesColorBgStaticContainerSubtle,
    width: theme.palette.size.width.column.column6,
    minHeight: theme.palette.size.width.column.column4,
    height: isActionCheck ? 'auto' : theme.palette.size.width.column.column4,
    position: 'relative',
    borderRadius: '4px',
    boxShadow: '0 2px 5px 0 rgba(0,0,0,0.26), 0 2px 10px 0 rgba(0,0,0,0.16)'
}));
const ListContainer = styled(Grid)(() => ({
    overflowY: 'scroll',
    display: 'flex',
    flex: 1
}));
const ButtonContainer = styled(Grid, {
    shouldForwardProp: prop => prop !== 'isSuccessAlert'
})(({ theme, isSuccessAlert }) => ({
    paddingTop: theme.palette.space.container.default,
    gap: isSuccessAlert ? theme.palette.space.container.dense : theme.palette.space.container.default,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'nowrap'
}));
const SearchFieldStyled = styled(SearchField)(({ theme }) => ({
    paddingBottom: theme.palette.space.container.default
}));
const StateUI = styled(Box)(({ theme }) => ({
    padding: theme.palette.space.container.default,
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center'
}));
const AccountSwitcher = ({ accounts, button1Text, button2Text, button3Text, onButton2Click, onButton3Click, initAccount, onSelectAccountCallback, addMSTenantText, manageAccountText, onManageAccountClick, onAddMSTenantClick, closeText = 'Close', onRename, onDelete, renderErrorState, renderNoResultsState, backText = 'Back' }) => {
    var _a, _b, _c, _d;
    const theme = useTheme();
    const [state, eventHandlers, filteredAccountsList] = useAccountSwitcherLogic({
        accounts,
        initAccount,
        onSelectAccountCallback,
        onRename,
        onDelete
    });
    // Create new variables for the updated props, maintaining the original ones as they are
    let newCloseText = closeText;
    let newAddMSTenantText = addMSTenantText;
    let newManageAccountText = manageAccountText;
    let newOnAddMSTenantClick = onAddMSTenantClick;
    let newOnManageAccountClick = onManageAccountClick;
    // Conditionally handle deprecation warnings and assign new values if the deprecated props are set
    // Handling 'button1Text' -> 'closeText'
    if (button1Text && !closeText) {
        logDeprecationWarning('AccountSwitcher', "'button1Text' is deprecated. Use 'closeText' instead.");
        newCloseText = button1Text; // Assign to the new variable
    }
    // Handling 'button2Text' -> 'manageAccountText'
    if (button2Text && !manageAccountText) {
        logDeprecationWarning('AccountSwitcher', "'button2Text' is deprecated. Use 'manageAccountText' instead.");
        newManageAccountText = button2Text; // Assign to the new variable
    }
    // Handling 'button3Text' -> 'addMSTenantText'
    if (button3Text && !addMSTenantText) {
        logDeprecationWarning('AccountSwitcher', "'button3Text' is deprecated. Use 'addMSTenantText' instead.");
        newAddMSTenantText = button3Text; // Assign to the new variable
    }
    // Handling 'onButton2Click' -> 'onManageAccountClick'
    if (onButton2Click && !onManageAccountClick) {
        logDeprecationWarning('AccountSwitcher', "'onButton2Click' is deprecated. Use 'onManageAccountClick' instead.");
        newOnManageAccountClick = onButton2Click; // Assign to the new variable
    }
    // Handling 'onButton3Click' -> 'onAddMSTenantClick'
    if (onButton3Click && !onAddMSTenantClick) {
        logDeprecationWarning('AccountSwitcher', "'onButton3Click' is deprecated. Use 'onAddMSTenantClick' instead.");
        newOnAddMSTenantClick = onButton3Click; // Assign to the new variable
    }
    const hasError = (renderErrorState === null || renderErrorState === void 0 ? void 0 : renderErrorState.hasError) || false;
    const renderStateUI = (icon, title, message) => (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column' }, "data-testid": "state-ui" },
        React.createElement(Box, { sx: { paddingBottom: theme.palette.space.component.default } }, icon),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: theme.palette.space.component.particle } },
            React.createElement(Typography, { variant: "body1" }, title),
            React.createElement(Typography, { variant: "captionNeutral" }, message))));
    const errorUI = renderStateUI(React.createElement(CustomIcon, { bdsType: "errorNeutral", bdsIcon: ErrorIcon, bdsSize: "xlarge" }), (_a = renderErrorState === null || renderErrorState === void 0 ? void 0 : renderErrorState.errorTitle) !== null && _a !== void 0 ? _a : 'Error loading accounts', (_b = renderErrorState === null || renderErrorState === void 0 ? void 0 : renderErrorState.errorMessage) !== null && _b !== void 0 ? _b : 'Close the switcher and reopen');
    const noResultsUI = state.hasNoResults &&
        renderStateUI(React.createElement(CustomIcon, { bdsType: "staticNeutral", bdsIcon: SearchIcon, bdsSize: "xlarge" }), (_c = renderNoResultsState === null || renderNoResultsState === void 0 ? void 0 : renderNoResultsState.noResultsTitle) !== null && _c !== void 0 ? _c : 'No results', (_d = renderNoResultsState === null || renderNoResultsState === void 0 ? void 0 : renderNoResultsState.noResultsMessage) !== null && _d !== void 0 ? _d : 'Try narrowing your search terms');
    const isActionCheck = state.actionView === 'rename' || state.actionView === 'delete';
    const isSuccessAlert = !!state.successText;
    return useMemo(() => {
        var _a, _b, _c, _d, _e;
        return (React.createElement(Container, { container: true, justifyContent: "center", alignItems: "center", accountSwitcherState: state.accountSwitcherState },
            state.accountSwitcherState === AccountSwitcherStates.minimized && (React.createElement(Grid, { item: true, style: { position: 'relative' } },
                React.createElement(AccountSwitcherWrapper, { labelId: "account-selector", "data-testid": "account-selector", onOpen: eventHandlers.onToggleSwitcherState, onClose: eventHandlers.onToggleSwitcherState, value: (_a = state.activeAccount) === null || _a === void 0 ? void 0 : _a.id, "aria-controls": "account-switcher", "aria-errormessage": hasError ? 'accounts-load-error' : undefined },
                    React.createElement(SelectMenuItem, { selected: ((_b = state.activeAccount) === null || _b === void 0 ? void 0 : _b.id) === ((_c = state.activeAccount) === null || _c === void 0 ? void 0 : _c.id), value: (_d = state.activeAccount) === null || _d === void 0 ? void 0 : _d.id }, ((_e = state.activeAccount) === null || _e === void 0 ? void 0 : _e.name) || (hasError ? 'Unable to Load Accounts' : ''))))),
            state.accountSwitcherState === AccountSwitcherStates.opened && (React.createElement(React.Fragment, null,
                React.createElement(Backdrop, { open: true, onClick: eventHandlers.onToggleSwitcherState, "aria-expanded": state.accountSwitcherState === AccountSwitcherStates.opened }),
                React.createElement(OpenContainer, { container: true, item: true, direction: "column", justifyContent: "flex-start", isActionCheck: isActionCheck },
                    !hasError && state.actionView !== 'rename' && state.actionView !== 'delete' && (React.createElement(Grid, { item: true },
                        React.createElement(SearchFieldStyled, { id: "account-switcher-search", placeholder: "Search", value: state.search, onChange: eventHandlers.onSearch }))),
                    hasError || state.hasNoResults ? (React.createElement(StateUI, { sx: {
                            padding: theme.palette.space.container.default,
                            display: 'flex',
                            flex: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                        } }, hasError ? errorUI : noResultsUI)) : (React.createElement(React.Fragment, null,
                        state.actionView === 'none' && (React.createElement(React.Fragment, null,
                            React.createElement(ListContainer, { container: true, item: true, direction: "column" },
                                React.createElement(MenuList, { "aria-label": "accounts", sx: { padding: 0 } },
                                    state.activeAccount && [
                                        state.activeAccount.parentId && (React.createElement(MenuItem, { key: "parent" },
                                            React.createElement(Box, { sx: { paddingRight: theme.palette.space.container.dense } },
                                                React.createElement(Icon, { bdsSize: "small", bdsType: "staticEmphasis", bdsIcon: CheckCircleIcon })),
                                            state.activeAccount.parentName)),
                                        React.createElement(MenuItem, { key: "active", sx: { paddingLeft: state.activeAccount.parentId ? '32px' : 0 } },
                                            React.createElement(Box, { sx: { paddingRight: theme.palette.space.container.dense } },
                                                React.createElement(Icon, { bdsSize: "small", bdsType: "staticEmphasis", bdsIcon: CheckCircleIcon })),
                                            state.activeAccount.name)
                                    ],
                                    filteredAccountsList)))),
                        state.actionView !== 'none' && state.selectedWorkspace && (React.createElement(Grid, { item: true, container: true, direction: "column", sx: {
                                height: 'auto',
                                padding: theme.palette.space.container.spacious,
                                gap: theme.palette.space.component.spacious
                            } },
                            React.createElement(Grid, { item: true },
                                React.createElement(Typography, { variant: "h3" }, state.actionTitle)),
                            state.actionView === 'rename' ? (React.createElement(Grid, { item: true },
                                React.createElement(TextField, { label: "Workspace name", fullWidth: true, value: state.selectedWorkspace.name, onChange: e => { var _a; return (_a = eventHandlers.onUpdateRenameField) === null || _a === void 0 ? void 0 : _a.call(eventHandlers, e.target.value); } }))) : (React.createElement(Grid, { item: true, container: true, direction: "column", alignItems: "center", gap: theme.palette.space.component.default },
                                React.createElement(Grid, { item: true },
                                    React.createElement(DeleteIllustration, null)),
                                React.createElement(Grid, { item: true },
                                    React.createElement(Typography, { variant: "body1" }, state.confirmationText)))))))),
                    React.createElement(Box, { sx: { marginTop: 'auto' } },
                        React.createElement(Divider, null),
                        React.createElement(ButtonContainer, { container: true, isSuccessAlert: isSuccessAlert },
                            React.createElement(Grid, { item: true }, state.successText ? (React.createElement(Alert, { bdsType: "neutralSuccess" }, "Workspace deleted!")) : (React.createElement(Grid, { item: true },
                                React.createElement(Button, { bdsType: "interactiveSubtle", onClick: eventHandlers.onToggleSwitcherState, "data-testid": "cancel-button" }, newCloseText)))),
                            (state.actionView === 'rename' || state.actionView === 'delete') && (React.createElement(Grid, { item: true },
                                React.createElement(Button, { bdsType: "interactiveNeutral", onClick: eventHandlers.onResetAction, "data-testid": "back-button", startIcon: React.createElement(BackIcon, null) }, backText))),
                            state.actionView === 'none' && addMSTenantText && (React.createElement(Grid, { item: true },
                                React.createElement(Button, { bdsType: "interactiveSubtle", onClick: newOnAddMSTenantClick, "data-testid": "add-ms-tenant-button", startIcon: React.createElement(AddCircleIcon, null) }, newAddMSTenantText))),
                            state.actionView === 'none' && manageAccountText && (React.createElement(Grid, { item: true },
                                React.createElement(Button, { bdsType: "interactiveNeutral", endIcon: React.createElement(LaunchIcon, null), onClick: newOnManageAccountClick, "data-testid": "manage-account-button" }, newManageAccountText))),
                            (state.actionView === 'rename' || state.actionView === 'delete') && (React.createElement(Grid, { item: true },
                                React.createElement(Button, { bdsType: "interactiveEmphasis", onClick: eventHandlers.onConfirmAction }, state.actionButtonText))))))))));
    }, [
        button1Text,
        button2Text,
        button3Text,
        addMSTenantText,
        eventHandlers,
        filteredAccountsList,
        manageAccountText,
        newAddMSTenantText,
        newCloseText,
        newManageAccountText,
        newOnAddMSTenantClick,
        newOnManageAccountClick,
        onButton2Click,
        onButton3Click,
        state.accountSwitcherState,
        state.actionButtonText,
        state.actionTitle,
        state.actionView,
        state.activeAccount,
        state.search,
        state.selectedWorkspace,
        state.successText
    ]);
};
export default AccountSwitcher;
//# sourceMappingURL=AccountSwitcher.js.map